/**
 * Simplified ColorManager for Sunburst Charts
 * Strategy: Distinct colors at level 1, then shade variations for descendants
 */
export class ColorManager {
  constructor() {
    this.baseColors = [
      '#3b82f6', // blue
      '#10b981', // green
      '#f59e0b', // amber
      '#ef4444', // red
      '#8b5cf6', // purple
      '#ec4899', // pink
      '#14b8a6', // teal
      '#f97316', // orange
      '#6366f1', // indigo
      '#84cc16', // lime
      '#06b6d4', // cyan
      '#a855f7', // violet
    ];
    this.rootColor = '#6366f1';
    this.colorCache = new Map();
  }

  reset() {
    this.colorCache.clear();
  }

  getRootColor() {
    return this.rootColor;
  }

  /**
   * Get color for a node based on its position in hierarchy
   * @param {Object} node - D3 hierarchy node with depth and path info
   * @param {string} firstLevelAncestorId - ID of the first-level ancestor
   * @param {number} firstLevelIndex - Index among first-level siblings (for base color)
   * @returns {string} Hex color
   */
  getColorForNode(node, firstLevelAncestorId, firstLevelIndex = 0) {
    const cacheKey = node.data.path;
    if (this.colorCache.has(cacheKey)) {
      return this.colorCache.get(cacheKey);
    }

    let color;

    if (node.depth === 0) {
      // Root
      color = this.rootColor;
    } else if (node.depth === 1) {
      // First level - use distinct base colors
      color = this.baseColors[firstLevelIndex % this.baseColors.length];
    } else {
      // Deeper levels - create variations of ancestor's color
      // Get the base color from first-level ancestor
      const baseColor = this.baseColors[firstLevelIndex % this.baseColors.length];

      // Calculate lightness variation based on depth
      // Each level gets progressively lighter
      const lightnessIncrease = (node.depth - 1) * 10;
      color = this.adjustLightness(baseColor, lightnessIncrease);
    }

    this.colorCache.set(cacheKey, color);
    return color;
  }

  /**
   * Adjust the lightness of a color
   * @param {string} hex - Hex color
   * @param {number} amount - Amount to increase lightness (0-100)
   * @returns {string} Adjusted hex color
   */
  adjustLightness(hex, amount) {
    const { h, s, l } = this.hexToHsl(hex);
    const newL = Math.min(85, l + amount); // Cap at 85% to keep visible
    return this.hslToHex(h, s, newL);
  }

  /**
   * Convert hex to HSL
   */
  hexToHsl(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return { h: 0, s: 50, l: 50 };

    const r = parseInt(result[1], 16) / 255;
    const g = parseInt(result[2], 16) / 255;
    const b = parseInt(result[3], 16) / 255;

    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;

    if (max === min) {
      h = s = 0;
    } else {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

      switch (max) {
        case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
        case g: h = ((b - r) / d + 2) / 6; break;
        case b: h = ((r - g) / d + 4) / 6; break;
      }
    }

    return {
      h: h * 360,
      s: s * 100,
      l: l * 100
    };
  }

  /**
   * Convert HSL to hex
   */
  hslToHex(h, s, l) {
    h = h / 360;
    s = s / 100;
    l = l / 100;

    let r, g, b;

    if (s === 0) {
      r = g = b = l;
    } else {
      const hue2rgb = (p, q, t) => {
        if (t < 0) t += 1;
        if (t > 1) t -= 1;
        if (t < 1/6) return p + (q - p) * 6 * t;
        if (t < 1/2) return q;
        if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
        return p;
      };

      const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
      const p = 2 * l - q;

      r = hue2rgb(p, q, h + 1/3);
      g = hue2rgb(p, q, h);
      b = hue2rgb(p, q, h - 1/3);
    }

    const toHex = x => {
      const hex = Math.round(x * 255).toString(16);
      return hex.length === 1 ? '0' + hex : hex;
    };

    return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
  }

  /**
   * Get contrasting text color for a background
   */
  getContrastingTextColor(hexColor) {
    const { l } = this.hexToHsl(hexColor);
    return l > 50 ? '#000000' : '#FFFFFF';
  }
}
